import { Action } from 'redux';
import { ActionPattern, call, select, StrictEffect, throttle } from 'redux-saga/effects';

/**
 * Saga function that persists Redux state using a web storage interface.
 *
 * Options:
 *
 * - `action`: The saga waits for Redux actions to dispatch to determine when to run the next persistence cycle. By default, it will respond to any action, but you can use the `action` config option to narrow to one or more specific action types.
 * - `selector`: A `selector` must be specified to determine exactly what state to persist to storage. No default selector is supplied; you must either use an existing selector, or else define one inline. If you want to store the entire store state, simply use the identify function: `(state) => state`. State is serialized into a string using `JSON.stringify()` regardless of the output of the selector.
 * - `storage`: Any type of `storage` interface may be used whether its a standard one such as `window.localStorage` or a custom-defined one. The only requirement is that it matches the web standard `Storage` interface.
 * - `storageKey`: Specify the key within the storage interface wherein the output of the selector will be stored.
 * - `throttle`: Persistence cycles will never run any more often than the given `throttle` time, which defaults to 1 second.
 *
 * @example
 * ```ts
 * import { spawn } from 'redux-saga/effects';
 * import { persistStateToWebStorage } from '@rcm/redux-saga-utils';
 *
 * function* saga() {
 *     // Spawn a new independent saga to persist into the key 'my.input1'
 *     yield spawn(persistStateToWebStorage, {
 *         action: 'INPUT1_CHANGED',
 *         key: 'my.input1',
 *         selector: (state) => state.input1,
 *         storage: window.localStorage,
 *     });
 * }
 * ```
 */
const persistStateToWebStorage = function* (cfg: {
    /**
     * Optional action pattern to listen for.
     *
     * This may be any value accepted by `take()`, such as a string or a
     * matcher function.
     *
     * The payload of the action is not used.
     *
     * Defaults to '*' (all actions).
     */
    action?: ActionPattern<Action<any>>;

    /**
     * Selector function accepting the current store state and returning the data to be persisted.
     */
    selector: (state: any) => any,

    /**
     * Object implementing the the web storage `Storage` interface.
     */
    storage: Storage;

    /**
     * Local storage key for the data to be persisted to.
     */
     storageKey: string;

    /**
     * Minimum time between synchronizations, in milliseconds.
     *
     * Defaults to 1000.
     */
    throttle?: number
}): Generator<StrictEffect> {
    const action = cfg.action ?? '*';
    const ms = cfg.throttle ?? 1000;

    const worker = function* () {
        const value = yield select(cfg.selector);
        const sValue = JSON.stringify(value);
        yield call(
            [cfg.storage, cfg.storage.setItem],
            cfg.storageKey,
            sValue
        );
    };

    yield throttle(ms, action, worker);
};

export default persistStateToWebStorage;
